// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList from "@insite/content-library/Components/CardList";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Notify from "@pace/App/Components/Notify";
import { xIsAuthenticated } from "@pace/App/Store/Selectors/GeneralSelectors";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    sectionTitle = "sectionTitle",
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.sectionTitle]: string;
    };
}

const recentWishListsParameter: GetWishListsApiParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    pageSize: 6,
    expand: ["top3products"],
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    recentWishListsDataView: getWishListsDataView(state, {
        ...recentWishListsParameter,
        pageSize: ownProps.fields.numberOfRecords || 6,
    }),
    myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
    canViewWishLists: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentNavWishListsStyles {
    gridContainer?: GridContainerProps;
    spinnerGridItem?: GridItemProps;
    wishListCardGridItem?: GridItemProps;
    notify?: TypographyPresentationProps;
    loadMoreGridItem?: GridItemProps;
    loadMoreLink?: LinkPresentationProps;
    title?: TypographyPresentationProps;
}

export const recentNavWishListsStyles: RecentNavWishListsStyles = {};

const styles = recentNavWishListsStyles;

const NavWishLists: FC<Props> = ({
    canViewWishLists,
    loadWishLists,
    myListsPageNavLink,
    recentWishListsDataView,
    fields,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    recentWishListsParameter.pageSize = fields.numberOfRecords || 6;
    useEffect(() => {
        if (canViewWishLists && !recentWishListsDataView.value && !recentWishListsDataView.isLoading) {
            setIsLoading(true);
            loadWishLists({
                ...recentWishListsParameter,
                onComplete: result => {
                    setIsLoading(false);
                },
            });
        }
    }, []);

    if (isLoading) {
        return (
            <CardList data-test-selector="cardListRecentWishLists" {...styles.gridContainer}>
                <GridItem {...styles.spinnerGridItem}>
                    <LoadingSpinner size={32} />
                </GridItem>
            </CardList>
        );
    }

    if (!recentWishListsDataView.value || !canViewWishLists || recentWishListsDataView.value.length === 0) {
        return (
            <CardList data-test-selector="cardListRecentWishLists" {...styles.gridContainer}>
                <GridItem width={12}>
                    <Typography {...styles.title} className="navigation-list-title">
                        {fields.sectionTitle}
                    </Typography>
                </GridItem>
                <GridItem width={12}>
                    <Notify type="info" {...styles.notify} showClose={false}>
                        {translate("No Lists Found.")}
                    </Notify>
                </GridItem>
            </CardList>
        );
    }

    return (
        <CardList data-test-selector="cardListRecentWishLists" {...styles.gridContainer}>
            <GridItem width={12}>
                <Typography {...styles.title} className="navigation-list-title">
                    {fields.sectionTitle}
                </Typography>
            </GridItem>
            {recentWishListsDataView.value?.map(wishList => (
                <GridItem
                    {...styles.wishListCardGridItem}
                    key={wishList.id}
                    data-test-selector="cardListRecentWishLists_container"
                >
                    <MyListsDetailsPageTypeLink
                        title={wishList.name}
                        wishListId={wishList.id}
                        testSelector="wishListCardName"
                    />
                </GridItem>
            ))}
        </CardList>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NavWishLists),
    definition: {
        allowedContexts: ["MyAccountPage"],
        displayName: "WishLists Navigation",
        group: "My Account",
        icon: "List",
        fieldDefinitions: [
            {
                name: fields.sectionTitle,
                fieldType: "Contextual",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "My Lists",
                sortOrder: 1,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 6,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
